import { getSortingStateParser } from '@shared/lib/parsers';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { z } from 'zod';

export const sortSchema = z.object({
  id: z.string(),
  desc: z.boolean(),
});

/**
 * Common filter parsers for reuse in custom hooks
 */
export const commonFilterParsers = {
  site: parseAsArrayOf(parseAsString).withDefault([]),
  supplier: parseAsArrayOf(parseAsString).withDefault([]),
  customer: parseAsArrayOf(parseAsString).withDefault([]),
  period: parseAsArrayOf(parseAsString).withDefault([]),
  status: parseAsArrayOf(parseAsString).withDefault([]),
  category: parseAsArrayOf(parseAsString).withDefault([]),
  type: parseAsArrayOf(parseAsString).withDefault([]),
  errors: parseAsArrayOf(parseAsString).withDefault([]),
  activitiesImport: parseAsArrayOf(parseAsString).withDefault([]),
  tag: parseAsArrayOf(parseAsString).withDefault([]),
  fuel: parseAsArrayOf(parseAsString).withDefault([]),
  emissionsCategory: parseAsArrayOf(parseAsString).withDefault([]),
  search: parseAsString,
} as const;

export type FilterKey = keyof typeof commonFilterParsers;

interface UseDataTableQueryStateOptions {
  defaultPage?: number;
  defaultPerPage?: number;
  filters?: FilterKey[];
}

/**
 * Unified hook for data table query state with customizable filters
 *
 * @param options Configuration object
 * @param options.defaultPage Default page number (default: 1)
 * @param options.defaultPerPage Default items per page (default: 10)
 * @param options.filters Array of filter keys to include (default: [])
 *
 * @example
 * // Basic usage (page, perPage, sort only)
 * const { page, perPage, sort } = useDataTableQueryState();
 *
 * @example
 * // With errors filter
 * const { page, perPage, sort, errors } = useDataTableQueryState({
 *   filters: ['errors']
 * });
 *
 * @example
 * // With common filters
 * const { page, perPage, sort, errors, site, supplier, period } = useDataTableQueryState({
 *   filters: ['errors', 'site', 'supplier', 'period']
 * });
 */
export function useDataTableQueryState(
  options: UseDataTableQueryStateOptions = {}
) {
  const { defaultPage = 1, defaultPerPage = 10, filters = [] } = options;

  const [page] = useQueryState('page', parseAsInteger.withDefault(defaultPage));
  const [perPage] = useQueryState(
    'perPage',
    parseAsInteger.withDefault(defaultPerPage)
  );
  const [sort] = useQueryState('sort', getSortingStateParser());

  // Create filter states - we need to call hooks unconditionally
  const [errors] = useQueryState('errors', commonFilterParsers.errors);
  const [site] = useQueryState('site', commonFilterParsers.site);
  const [supplier] = useQueryState('supplier', commonFilterParsers.supplier);
  const [customer] = useQueryState('customer', commonFilterParsers.customer);
  const [period] = useQueryState('period', commonFilterParsers.period);
  const [status] = useQueryState('status', commonFilterParsers.status);
  const [category] = useQueryState('category', commonFilterParsers.category);
  const [type] = useQueryState('type', commonFilterParsers.type);
  const [fuels] = useQueryState('fuel', commonFilterParsers.type);
  const [activitiesImport] = useQueryState(
    'activitiesImport',
    commonFilterParsers.activitiesImport
  );
  const [tag] = useQueryState('tag', commonFilterParsers.tag);
  const [emissionsCategory] = useQueryState(
    'emissionsCategory',
    commonFilterParsers.emissionsCategory
  );
  const [search] = useQueryState('search', commonFilterParsers.search);

  // Build the return object based on requested filters
  const filterStates = {} as Record<FilterKey, any>;
  filters.forEach((filterKey) => {
    switch (filterKey) {
      case 'search':
        filterStates.search = search;
        break;
      case 'errors':
        filterStates.errors = errors;
        break;
      case 'site':
        filterStates.site = site;
        break;
      case 'supplier':
        filterStates.supplier = supplier;
        break;
      case 'customer':
        filterStates.customer = customer;
        break;
      case 'period':
        filterStates.period = period;
        break;
      case 'status':
        filterStates.status = status;
        break;
      case 'category':
        filterStates.category = category;
        break;
      case 'type':
        filterStates.type = type;
        break;
      case 'fuel':
        filterStates.fuel = fuels;
        break;
      case 'activitiesImport':
        filterStates.activitiesImport = activitiesImport;
        break;
      case 'tag':
        filterStates.tag = tag;
        break;
      case 'emissionsCategory':
        filterStates.emissionsCategory = emissionsCategory;
        break;
      // No default
    }
  });

  return {
    page,
    perPage,
    sort,
    ...filterStates,
  };
}

/**
 * @deprecated Use useDataTableQueryState with filters: ['errors'] instead
 */
export function useDataTableQueryStateWithErrors(
  options: {
    defaultPage?: number;
    defaultPerPage?: number;
  } = {}
) {
  return useDataTableQueryState({
    ...options,
    filters: ['errors'],
  });
}
